// src/services/auth.js
// --- Auth para HashRouter (#/...) robusto y sin bucles ---

const TOKEN_KEY   = "access_token";
const ROLE_KEY    = "asotrans_role";
const SOCIO_KEY   = "asotrans_socio_id";
const NEXT_KEY    = "asotrans_next_path";

// ------------------ utils ------------------
function base64UrlToJson(b64url) {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const json = decodeURIComponent(
    atob(b64)
      .split("")
      .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join("")
  );
  return JSON.parse(json);
}
function decodeJwt(t) {
  try {
    const [, payload] = t.split(".");
    if (!payload) return {};
    return base64UrlToJson(payload);
  } catch { return {}; }
}
function isLoginHash(h) {
  return h === "#/login" || h.startsWith("#/login?");
}
function currentHash() {
  try { return window.location.hash || "#/"; } catch { return "#/"; }
}
function setNextIfNotLogin() {
  try {
    const h = currentHash();
    if (!isLoginHash(h)) sessionStorage.setItem(NEXT_KEY, h);
  } catch {}
}
function getAndClearNext() {
  let n = null;
  try { n = sessionStorage.getItem(NEXT_KEY) || null; } catch {}
  if (n && isLoginHash(n)) n = null;
  try { sessionStorage.removeItem(NEXT_KEY); } catch {}
  return n;
}
function navReplace(hashDest) {
  if (typeof window === "undefined") return;
  if (window.location.hash !== hashDest) {
    window.location.replace(hashDest);
  } else {
    // fuerza hidratación si ya estás justo ahí
    window.location.reload();
  }
}

// ------------------ token ------------------
export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || null;
}
export function setToken(token, persist = true) {
  const storage = persist ? localStorage : sessionStorage;
  storage.setItem(TOKEN_KEY, token);
  // Cookie para compartir sesion con sub-apps (liquidaciones, fiscal) del mismo dominio
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = TOKEN_KEY + "=" + token + "; Path=/; SameSite=Lax" + secure + "; Max-Age=28800";
  } catch {}
}
export function clearToken() {
  for (const k of [TOKEN_KEY, ROLE_KEY, SOCIO_KEY]) {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  }
  try { document.cookie = TOKEN_KEY + "=; Path=/; Max-Age=0"; } catch {}
}

// ---------------- identidad / roles ----------------
export function getIdentity() {
  const t = getToken();
  if (!t) return {};
  const p = decodeJwt(t);
  return {
    sub: p.sub ?? null,
    rol: p.rol ?? p.role ?? null,
    socio_id: p.socio_id ?? null,
    exp: p.exp ?? null,
  };
}
export function getRole() {
  return (
    localStorage.getItem(ROLE_KEY) ||
    sessionStorage.getItem(ROLE_KEY) ||
    getIdentity().rol ||
    null
  );
}
export function getSocioId() {
  const v =
    localStorage.getItem(SOCIO_KEY) ||
    sessionStorage.getItem(SOCIO_KEY) ||
    null;
  if (v == null) {
    const id = getIdentity().socio_id;
    return id == null ? null : parseInt(id, 10);
  }
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
}
export function isEmpleado() { return getRole() === "empleado"; }
export function isSocio()    { return getRole() === "socio"; }
export function isGestorFiscal() { return getRole() === "gestor_fiscal"; }
export function getUsername() { const t = getToken(); if (!t) return null; const [,p] = t.split("."); if (!p) return null; try { return JSON.parse(atob(p.replace(/-/g, "+").replace(/_/g, "/"))).sub; } catch { return null; } }

// Llama al backend para fijar rol/socio_id sin depender del decode
async function fetchMeAndPersist(persist) {
  const t = getToken();
  if (!t) return null;
  const headers = new Headers();
  headers.set("Authorization", `Bearer ${t}`);
  const r = await fetch("/api/auth/me", { headers });
  if (!r.ok) return null;
  const me = await r.json(); // espera { rol, socio_id, sub? ... }
  const storage = persist ? localStorage : sessionStorage;
  if (me?.rol)     storage.setItem(ROLE_KEY, me.rol);
  if (me?.socio_id !== undefined && me?.socio_id !== null) {
    storage.setItem(SOCIO_KEY, String(me.socio_id));
  }
  return me;
}

// ------------------ login ------------------
export async function login(username, password, persist = true) {
  // Limpieza de NEXT si por lo que sea quedó #/login
  try {
    const oldNext = sessionStorage.getItem(NEXT_KEY);
    if (oldNext && isLoginHash(oldNext)) sessionStorage.removeItem(NEXT_KEY);
  } catch {}

  const r = await fetch("/api/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (!r.ok) throw new Error("Login inválido");

  const data = await r.json(); // { access_token, token_type }
  const token = data?.access_token;
  if (!token) throw new Error("Token ausente");
  setToken(token, persist);

  // Obtener identidad real del backend
  const me = await fetchMeAndPersist(persist);

  // Destino: next guardado (si NO es /login) o fallback por rol
  const next = getAndClearNext();
  const rol = me?.rol || getRole();
  const socio_id = (me?.socio_id ?? getSocioId());
  const fallback = (rol === "socio" && socio_id) ? `#/socios/${socio_id}` : "#/";

  navReplace(next || fallback);
  return data;
}

// --------------- fetch autenticado ---------------
export async function authFetch(input, init = {}) {
  const t = getToken();
  const headers = new Headers(init.headers || {});
  if (t) headers.set("Authorization", `Bearer ${t}`);

  // Comprobación de exp local (si existe)
  const { exp } = getIdentity();
  if (exp && Date.now() / 1000 >= exp) {
    clearToken();
    setNextIfNotLogin();
    window.location.hash = "#/login";
    throw new Error("Token expirado");
  }

  const res = await fetch(input, { ...init, headers });

  if (res.status === 401) {
    setNextIfNotLogin();
    clearToken();
    if (!isLoginHash(currentHash())) {
      window.location.hash = "#/login";
    }
  }
  return res;
}
